/**
 * PeoplePrints application root.
 *
 * Phase 1 first ship: minimal router with a Home shell, a SignIn page (stub),
 * and an Operating Manual demo route that exercises the component library
 * with mock data so the design is reviewable without a backend round-trip.
 *
 * Real auth-gated routes (Profile pages, Blueprint generators, audit surface)
 * land as features come online.
 */

import { useEffect, useState } from "react";
import { Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import { Wordmark, ToastProvider } from "./components/index.js";
import DiagPanel from "./components/DiagPanel.jsx";
import ImpersonationBanner from "./components/ImpersonationBanner.jsx";
import ArchivedTenantBanner from "./components/ArchivedTenantBanner.jsx";
import { useSession } from "./lib/useSession.js";
import { supabase } from "./lib/supabase.js";
import { bootstrapImpersonationFromUrl } from "./lib/impersonation-client.js";

import HomePage from "./pages/Home.jsx";
import SignInPage from "./pages/SignIn.jsx";
import OperatingManualDemo from "./pages/OperatingManualDemo.jsx";
import ConsentLanding from "./pages/ConsentLanding.jsx";
import FairnessAuditPage from "./pages/FairnessAudit.jsx";
import ProfileListPage from "./pages/ProfileList.jsx";
import ProfileNewPage from "./pages/ProfileNew.jsx";
import ProfileBulkUploadPage from "./pages/ProfileBulkUpload.jsx";
import ProfileDetailPage from "./pages/ProfileDetail.jsx";
import SettingsIntegrationsPage from "./pages/SettingsIntegrations.jsx";
import OperatorProfilePage from "./pages/OperatorProfile.jsx";
// Team settings, Team Blueprint, Team Compare removed — single-seat model
import PreMeetingBlueprintPage from "./pages/PreMeetingBlueprintPage.jsx";
import ConflictMediationBriefPage from "./pages/ConflictMediationBriefPage.jsx";
import ResumeBlueprintPage from "./pages/ResumeBlueprintPage.jsx";
import RelationalBlueprintPage from "./pages/RelationalBlueprintPage.jsx";
import NegotiationBlueprintPage from "./pages/NegotiationBlueprintPage.jsx";
import CommonGroundPage from "./pages/CommonGroundPage.jsx";
import ThoughtEngineeringPage from "./pages/ThoughtEngineeringPage.jsx";
import TeamBlueprintPage from "./pages/TeamBlueprintPage.jsx";
import NotificationsInbox from "./pages/NotificationsInbox.jsx";
import OnboardingPage from "./pages/Onboarding.jsx";
import OnboardingNoTenant from "./pages/OnboardingNoTenant.jsx";
import RevokePage from "./pages/RevokePage.jsx";
import RevokeViaTokenPage from "./pages/RevokeViaTokenPage.jsx";
import PartnerThemeDemo from "./pages/PartnerThemeDemo.jsx";
import TenantBootstrapPage from "./pages/TenantBootstrap.jsx";
import TenantThemeAdminPage from "./pages/TenantThemeAdmin.jsx";
import BillingAdminPage from "./pages/BillingAdmin.jsx";
import NotFound from "./pages/NotFound.jsx";
import JoinAcceptPage from "./pages/JoinAcceptPage.jsx";
import BlogIndexPage from "./pages/BlogIndex.jsx";
import BlogPostPage from "./pages/BlogPost.jsx";
import FieldNotesPage from "./pages/FieldNotesPage.jsx";
import FieldNotesIssuePage from "./pages/FieldNotesIssuePage.jsx";
import MarketingPageScaffold from "./pages/marketing/MarketingPageScaffold.jsx";
import PricingPage from "./pages/marketing/PricingPage.jsx";
import MethodologyPage from "./pages/marketing/MethodologyPage.jsx";
import AboutPage from "./pages/marketing/AboutPage.jsx";
import SecurityPrivacyPage from "./pages/marketing/SecurityPrivacyPage.jsx";
import DemoPage from "./pages/marketing/DemoPage.jsx";
import SpeakingPage from "./pages/marketing/SpeakingPage.jsx";
import ForRecruitersPage from "./pages/marketing/ForRecruitersPage.jsx";
import ForManagersPage from "./pages/marketing/ForManagersPage.jsx";
import ForHRDirectorsPage from "./pages/marketing/ForHRDirectorsPage.jsx";
import ForFoundersPage from "./pages/marketing/ForFoundersPage.jsx";
import ForEmployeeRelationsPage from "./pages/marketing/ForEmployeeRelationsPage.jsx";
import HRDirectorsGuidePage from "./pages/marketing/HRDirectorsGuidePage.jsx";
import VsCrystalKnowsPage from "./pages/marketing/VsCrystalKnowsPage.jsx";
import VsPredictiveIndexPage from "./pages/marketing/VsPredictiveIndexPage.jsx";
import SampleBlueprintPage from "./pages/marketing/SampleBlueprintPage.jsx";
import SampleBlueprintResumePage from "./pages/marketing/SampleBlueprintResumePage.jsx";
import SampleBlueprintConflictMediationPage from "./pages/marketing/SampleBlueprintConflictMediationPage.jsx";
import SampleBlueprintOperatingManualPage from "./pages/marketing/SampleBlueprintOperatingManualPage.jsx";
import SampleBlueprintRelationalPage from "./pages/marketing/SampleBlueprintRelationalPage.jsx";
import SampleBlueprintTeamPage from "./pages/marketing/SampleBlueprintTeamPage.jsx";
import { MARKETING_ROUTES } from "./lib/marketing-routes.js";

// Registry of marketing routes whose real page has been built — App renders
// the real component for these and the scaffold for the rest. Keep in sync
// with `status: "built"` in marketing-routes.js (that flag drives the
// prerender allowlist + sitemap; this registry drives what renders).
// All 14 marketing pages are now built + registered — the two /vs/*
// competitor pages joined once their pre-publish fact-check pass cleared
// 2026-05-23 (see marketing-routes.js).
const MARKETING_PAGES = {
  "/pricing": PricingPage,
  "/methodology": MethodologyPage,
  "/about": AboutPage,
  "/security-and-privacy": SecurityPrivacyPage,
  "/demo": DemoPage,
  "/speaking": SpeakingPage,
  "/for/recruiters": ForRecruitersPage,
  "/for/managers": ForManagersPage,
  "/for/hr-directors": ForHRDirectorsPage,
  "/for/founders": ForFoundersPage,
  "/for/employee-relations": ForEmployeeRelationsPage,
  "/hr-directors-guide": HRDirectorsGuidePage,
  "/vs/crystal-knows": VsCrystalKnowsPage,
  "/vs/predictive-index": VsPredictiveIndexPage,
};

export default function App() {
  const { needsOnboarding } = useSession();
  // Impersonation bootstrap — runs ONCE on first render. If the URL carries
  // `?_imp=<token>` (set by the admin tab opening this surface in a new
  // tab), capture the token into sessionStorage, decode the claims, and
  // strip the query param from the URL. The ImpersonationBanner picks it
  // up below; api-fetch.js uses the stored token as bearer in place of
  // the normal Supabase session.
  const [, setImpHydrated] = useState(false);
  useEffect(() => {
    bootstrapImpersonationFromUrl();
    setImpHydrated(true);
  }, []);
  // Cold-signin guard: a Supabase session with no app_users row (and no
  // tenant_id resolvable from JWT/metadata for /api/signup-finalize) means
  // the user signed in without a magic-invite or tenant-admin provisioning.
  // Short-circuit every tenant-scoped route to the friendly invite-only
  // landing instead of letting `requireAuth` surface a raw 403.
  // See engineering/src/pages/OnboardingNoTenant.jsx for the framing.
  // The /join/:token accept page is reachable mid-flow by an invitee who
  // has just signed up — a Supabase session but no app_users row yet, i.e.
  // exactly the needsOnboarding state. It must NOT be short-circuited to the
  // no-tenant landing, or accepting a team invite becomes impossible (#75).
  const onJoinRoute = useLocation().pathname.startsWith("/join/");
  if (needsOnboarding && !onJoinRoute) {
    return (
      <ToastProvider>
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
          <ArchivedTenantBanner />
          <ImpersonationBanner />
          <TopNav />
          <main style={{ flex: 1 }}>
            <OnboardingNoTenant />
          </main>
          <DiagPanel />
        </div>
      </ToastProvider>
    );
  }
  return (
    <ToastProvider>
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <ArchivedTenantBanner />
      <ImpersonationBanner />
      <TopNav />
      <main style={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/onboarding/no-tenant" element={<OnboardingNoTenant />} />
          <Route
            path="/demo/operating-manual"
            element={<OperatingManualDemo />}
          />
          <Route path="/c/:token" element={<ConsentLanding />} />
          <Route path="/join/:token" element={<JoinAcceptPage />} />
          <Route path="/audit" element={<FairnessAuditPage />} />
          <Route path="/me" element={<OperatorProfilePage />} />
          <Route path="/profiles" element={<ProfileListPage />} />
          <Route path="/profiles/new" element={<ProfileNewPage />} />
          <Route path="/profiles/bulk-upload" element={<ProfileBulkUploadPage />} />
          <Route path="/profiles/:id" element={<ProfileDetailPage />} />
          {/* Calendar integration turned off 2026-06-08: it presumed stored
             DOBs + auto calendar scans (the PeoplePrints model). In Interaction Engineering a
             read only exists from a manually entered DOB, so a "connect your
             calendar" option promised automatic behavior that never happens.
             Route redirects to Billing; the page component is retained but
             unreachable. */}
          <Route path="/settings/integrations" element={<Navigate to="/admin/billing" replace />} />
          <Route path="/blueprints/pre-meeting" element={<PreMeetingBlueprintPage />} />
          <Route path="/blueprints/conflict-mediation" element={<ConflictMediationBriefPage />} />
          <Route path="/blueprints/counterpart-breakdown" element={<ResumeBlueprintPage />} />
          <Route path="/blueprints/relational" element={<RelationalBlueprintPage />} />
          <Route path="/blueprints/negotiation" element={<NegotiationBlueprintPage />} />
          <Route path="/blueprints/common-ground" element={<CommonGroundPage />} />
          <Route path="/blueprints/thought-engineering" element={<ThoughtEngineeringPage />} />
          <Route path="/blueprints/room" element={<TeamBlueprintPage />} />
          {/* Legacy slugs kept as redirects so old links/bookmarks still land.
             User-facing names are Counterpart Breakdown + Room Blueprint; the
             internal blueprint_type identifiers (resume / team) stay unchanged. */}
          <Route path="/blueprints/resume" element={<Navigate to="/blueprints/counterpart-breakdown" replace />} />
          <Route path="/blueprints/team" element={<Navigate to="/blueprints/room" replace />} />
          <Route path="/notifications" element={<NotificationsInbox />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/revoke" element={<RevokePage />} />
          <Route path="/revoke-via-token/:token" element={<RevokeViaTokenPage />} />
          <Route path="/demo/themes" element={<PartnerThemeDemo />} />
          <Route path="/admin/tenant-bootstrap" element={<TenantBootstrapPage />} />
          <Route path="/admin/theme" element={<TenantThemeAdminPage />} />
          <Route path="/admin/billing" element={<BillingAdminPage />} />
          {/* App-only build: marketing surface (blog, /for/* audience pages,
             methodology, sample-blueprints, Field Notes) lives on cersosimo.co,
             not here. pro.cersosimo.co is sign-in -> the product. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <SiteFooter />
      <DiagPanel />
    </div>
    </ToastProvider>
  );
}

// Footer — social profiles match cersosimo.co. Themed via tokens (light/dark).
const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/cersosimo-decision-science-engineering" },
  { label: "X",        href: "https://x.com/CersosimoDS" },
  { label: "Facebook", href: "https://www.facebook.com/cersosimo.decision.science" },
  { label: "TikTok",   href: "https://www.tiktok.com/@.decision.science" },
];

function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      style={{
        borderTop: "0.5px solid var(--ppr-color-border-default)",
        background: "var(--ppr-color-background-page)",
        padding: "20px 24px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 18,
      }}
    >
      <span
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: 14,
          color: "var(--ppr-color-text-primary)",
          letterSpacing: "-0.01em",
        }}
      >
        Cersosimo · Decision Science &amp; Engineering
      </span>
      <nav style={{ display: "flex", gap: 18, marginLeft: "auto", flexWrap: "wrap" }}>
        {SOCIALS.map((s) => (
          <a
            key={s.label}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "var(--ppr-typography-font-family-ui)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--ppr-color-text-secondary)",
              textDecoration: "none",
            }}
          >
            {s.label}
          </a>
        ))}
      </nav>
      <span
        style={{
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: 11,
          color: "var(--ppr-color-text-muted)",
          width: "100%",
        }}
      >
        © {year} Cersosimo and Associates LLC · Interaction Engineering
      </span>
    </footer>
  );
}

const navLinkStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: 12,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--ppr-color-text-secondary)",
  textDecoration: "none",
};

function TopNav() {
  const { session, appUser, loading, needsOnboarding } = useSession();
  // Hide tenant-scoped nav links when the user has no app_users row yet —
  // every link below would 403 against `requireAuth`. The in-place
  // OnboardingNoTenant render in App() handles the main area; TopNav has
  // to mirror that or the affordances lie about what works.
  const showTenantNav = !!session && !needsOnboarding;
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("tpm26-theme") === "dark"; } catch (_) { return false; }
  });
  const toggleTheme = () => {
    const next = dark ? "light" : "dark";
    setDark(!dark);
    if (next === "dark") document.documentElement.setAttribute("data-theme", "dark");
    else document.documentElement.removeAttribute("data-theme");
    try { localStorage.setItem("tpm26-theme", next); } catch (_) {}
  };
  return (
    <nav
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "12px 24px",
        borderBottom: "1px solid var(--ppr-color-border-default)",
        background: "var(--ppr-color-background-page)",
      }}
    >
      <Link
        to="/"
        style={{ textDecoration: "none", marginRight: "auto" }}
        aria-label="Interaction Engineering home"
      >
        <Wordmark scale={1.62} />
      </Link>
      {showTenantNav && (
        <>
          <Link to="/" style={navLinkStyle}>Dashboard</Link>
          <Link to="/profiles" style={navLinkStyle}>People</Link>
          <Link to="/me" style={navLinkStyle}>You</Link>
          {(appUser?.is_tenant_admin || appUser?.tier === "enterprise") && (
            <Link to="/admin/billing" style={navLinkStyle}>Billing</Link>
          )}
        </>
      )}
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={dark ? "Switch to day mode" : "Switch to night mode"}
        title={dark ? "Day mode" : "Night mode"}
        style={{
          fontFamily: "var(--ppr-typography-font-family-ui)",
          fontSize: "13px",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--ppr-color-text-secondary)",
          background: "transparent",
          border: "0.5px solid var(--ppr-color-border-default)",
          borderRadius: 6,
          cursor: "pointer",
          padding: "5px 10px",
          marginLeft: showTenantNav ? 0 : "auto",
        }}
      >
        {dark ? "Day" : "Night"}
      </button>
      {!loading && session ? (
        <>
          <span
            style={{
              fontFamily: "var(--ppr-typography-font-family-mono)",
              fontSize: "var(--ppr-typography-font-size-xs)",
              color: "var(--ppr-color-text-muted)",
            }}
          >
            {appUser?.display_name || session.user.email} · {appUser?.tier || "—"}
          </span>
          <button
            type="button"
            onClick={async () => {
              await supabase.auth.signOut();
              // Hard refresh so module-scoped finalize state + any in-memory
              // session caches are wiped. Avoids a sign-in/sign-out loop
              // where stale state survives across the auth boundary.
              window.location.assign("/");
            }}
            style={{
              fontFamily: "var(--ppr-typography-font-family-ui)",
              fontSize: "var(--ppr-typography-font-size-sm)",
              color: "var(--ppr-color-text-secondary)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px 8px",
              textDecoration: "underline",
            }}
            data-testid="customer-sign-out"
            aria-label="Sign out"
          >
            Sign out
          </button>
        </>
      ) : !loading ? (
        <Link
          to="/signin"
          style={{
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            padding: "8px 14px",
            border: "2px solid var(--ppr-color-accent-navy)",
            background: "var(--ppr-color-background-surface)",
            color: "var(--ppr-color-accent-navy)",
            textDecoration: "none",
          }}
        >
          Sign in
        </Link>
      ) : null}
    </nav>
  );
}
